import type {
  BaseResponse,
  Event,
  EventDetailParams,
  EventGuest,
  EventGuestDetailParams,
  EventPersonalMessageParams,
} from "@anggunpraya-templates/types";
import type {
  EventPersonalMessageBody,
  EventRsvpConfirmationBody,
} from "../schemas/event";

/**
 * Service domain `event`.
 *
 * Service ini mengelompokkan semua endpoint yang berhubungan dengan:
 * - detail event
 * - guest undangan
 * - personal message / wishes
 * - open invitation
 * - RSVP confirmation
 *
 * Semua method di sini memakai `$api` shared dan `handleErrorResponse`
 * agar perilaku error tetap konsisten antar template.
 */
export function serviceEvent() {
  const { $api } = useNuxtApp();
  const baseUrl = "/event";

  return {
    /**
     * Mengambil detail event berdasarkan parameter pencarian yang diberikan.
     */
    getDetail: async (params: EventDetailParams) => {
      return await $api<BaseResponse<Event>>(`${baseUrl}/detail`, {
        onResponseError: handleErrorResponse,
        params,
      });
    },

    /**
     * API terkait personal message / wishes dari guest undangan.
     */
    personalMessage: {
      /**
       * Mengambil daftar personal message / wishes milik sebuah event.
       */
      getAll: async (
        eventId: string,
        params: Partial<EventPersonalMessageParams>,
      ) => {
        return await $api<BaseResponse<Event, true>>(
          `${baseUrl}/${eventId}/personal-message`,
          {
            onResponseError: handleErrorResponse,
            params,
          },
        );
      },
      /**
       * Membuat personal message / wishes baru dari guest tertentu.
       */
      create: async (
        eventId: string,
        body: EventPersonalMessageBody,
      ) => {
        return await $api<BaseResponse<null>>(
          `${baseUrl}/${eventId}/personal-message`,
          {
            method: "POST",
            onResponseError: handleErrorResponse,
            body,
          },
        );
      },
    },

    /**
     * API terkait guest undangan.
     */
    guest: {
      /**
       * Mengambil detail guest undangan berdasarkan `code`.
       */
      getDetail: async (
        eventId: string,
        params: Partial<EventGuestDetailParams>,
      ) => {
        return await $api<BaseResponse<EventGuest>>(
          `${baseUrl}/${eventId}/guest/detail`,
          {
            onResponseError: handleErrorResponse,
            params,
          },
        );
      },
    },

    /**
     * Menandai bahwa guest sudah membuka undangan.
     */
    openInvitation: async (eventId: string, eventGuestId: string) => {
      return await $api<BaseResponse<null>>(
        `${baseUrl}/${eventId}/guest/${eventGuestId}/open-invitation`,
        {
          method: "POST",
          onResponseError: handleErrorResponse,
        },
      );
    },

    /**
     * Mengirim konfirmasi RSVP guest untuk event tertentu.
     */
    rsvpConfirmation: async (
      eventId: string,
      eventGuestId: string,
      body: EventRsvpConfirmationBody,
    ) => {
      return await $api<BaseResponse<null>>(
        `${baseUrl}/${eventId}/guest/${eventGuestId}/rsvp-confirmation`,
        {
          method: "POST",
          onResponseError: handleErrorResponse,
          body,
        },
      );
    },
  };
}
